/* LE BLU - gallery clones + viewport geometry v9
   Replace the ENTIRE contents of assets/leblu-product-gallery-new.js.

   Rules of this file:
   1. Geometry (header overlap, gallery top offset, panel top) is measured in
      DOCUMENT coordinates and ONLY while the page is at the top. Scrolling the
      page can never change it. A header that is sticky/fixed and resizes while
      scrolling is therefore ignored.
   2. The section height is not written here: CSS computes 100svh - top offset.
   3. This file never touches the track `animation` once leblu-gallery-interactions.js
      has taken over the horizontal motion (class .leblu-interactive-gallery).
   4. The first measurement is synchronous; the section is revealed
      (html.leblu-geometry-ready) only after it, so it is painted once, in place.

   Debug: open the page with ?leblu-debug=1 to log every measurement.
*/
(() => {
  'use strict';

  const MOBILE_MAX = 767;
  const READY_CLASS = 'leblu-geometry-ready';
  const AT_TOP = 2;            // px of scroll still considered "at the top"
  const REVEAL_TIMEOUT = 800;  // ms: max wait for web fonts before revealing
  const DEBUG = /[?&]leblu-debug/.test(location.search);

  const galleries = new WeakMap();
  const $ = (selector, scope = document) => {
    try { return scope.querySelector(selector); } catch (e) { return null; }
  };

  let snapshot = null;      // { width, provisional } of the last accepted measurement
  let geometryFrame = 0;
  let bound = false;
  let revealed = false;

  /* ------------------------------------------------------------------ */
  /* Gallery: clones for the infinite strip                              */
  /* ------------------------------------------------------------------ */
  function setupGallery(root) {
    const track = $('[data-leblu-track]', root);
    const original = track && $('[data-leblu-set]', track);
    if (!track || !original || galleries.has(root)) return;

    let frame = 0;
    let lastWidth = 0;

    function measure() {
      frame = 0;
      if (!root.isConnected) return;
      const width = original.getBoundingClientRect().width;
      const viewport = root.getBoundingClientRect().width;
      if (width <= 0 || viewport <= 0) return;

      const wanted = Math.max(2, Math.ceil(viewport / width) + 2) - 1; // clones, original excluded
      const existing = track.querySelectorAll('[data-leblu-clone]');
      let changed = false;

      // Add or remove only what is needed: no full rebuild, no image re-decode.
      for (let i = existing.length - 1; i >= wanted; i--) {
        existing[i].remove();
        changed = true;
      }
      if (existing.length < wanted) {
        const fragment = document.createDocumentFragment();
        for (let i = existing.length; i < wanted; i++) {
          const clone = original.cloneNode(true);
          clone.removeAttribute('data-leblu-set');
          clone.setAttribute('data-leblu-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('img').forEach(img => { img.alt = ''; img.removeAttribute('id'); });
          fragment.appendChild(clone);
        }
        track.appendChild(fragment);
        changed = true;
      }

      const distanceChanged = Math.abs(width - lastWidth) >= 0.5;
      lastWidth = width;
      track.style.setProperty('--leblu-distance', `${width}px`);
      track.style.setProperty('--leblu-duration', `${Math.max(18, width / 38)}s`);

      // CSS marquee = fallback only. When the interactive script owns the motion
      // we must not reset `animation`, or its `animation: none !important` is lost.
      if ((changed || distanceChanged) && !root.classList.contains('leblu-interactive-gallery')) {
        track.style.animation = 'none';
        void track.offsetWidth;
        track.style.removeProperty('animation');
      }
    }

    function scheduleMeasure() {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    }

    measure(); // synchronous: clones exist before the first reveal

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(scheduleMeasure);
      observer.observe(root);
      observer.observe(original);
      galleries.set(root, observer);
    } else {
      galleries.set(root, null);
      addEventListener('resize', scheduleMeasure, { passive: true });
    }
    original.querySelectorAll('img').forEach(img => {
      if (!img.complete) img.addEventListener('load', scheduleMeasure, { once: true });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Geometry: announcement bar / header / section                       */
  /* ------------------------------------------------------------------ */
  function isDetached(el) {
    for (let node = el; node && node !== document.body && node.nodeType === 1; node = node.parentElement) {
      const position = getComputedStyle(node).position;
      if (position === 'fixed' || position === 'sticky') return true;
    }
    return false;
  }

  // Bottom edge in document coordinates. Fixed/sticky elements are read in
  // viewport coordinates: at scroll-top the two coincide.
  function pageBottom(el) {
    const rect = el.getBoundingClientRect();
    return rect.bottom + (isDetached(el) ? 0 : window.scrollY);
  }

  function usableBar(el) {
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return rect.width > innerWidth * 0.4 && rect.height >= 15 && rect.height < 160 ? el : null;
  }

  function findAnnouncement() {
    const section = usableBar(
      $('#shopify-section-announcement-bar') ||
      $('[id^="shopify-section-"][id*="announcement"]') ||
      $('.shopify-section-group-header-group:has(.announcement-bar)')
    );
    if (section) return section;
    const candidates = [...document.querySelectorAll('.announcement-bar, [class*="announcement-bar"], [class*="announcement_bar"]')]
      .filter(usableBar)
      .filter(el => pageBottom(el) - el.getBoundingClientRect().height < 140)
      .sort((a, b) => pageBottom(a) - pageBottom(b));
    return candidates[0] || null;
  }

  function getNavigationBottom() {
    const candidates = [
      $('#shopify-section-header'),
      $('.shopify-section-group-header-group:has(header)'),
      $('header')
    ].filter(el => el && el.getBoundingClientRect().height > 20);
    return candidates.length ? Math.max(...candidates.map(pageBottom)) : 115;
  }

  function clearGeometryVars(html) {
    ['--leblu-header-overlap', '--leblu-top-offset', '--leblu-panel-top', '--leblu-viewport-height']
      .forEach(name => html.style.removeProperty(name));
  }

  function measureGeometry() {
    geometryFrame = 0;
    const product = $('.leblu-new-product');
    const outer = product && product.closest('.product__section');
    if (!product || !outer) return;
    const html = document.documentElement;

    if (innerWidth <= MOBILE_MAX) {
      clearGeometryVars(html);
      snapshot = null;
      html.classList.add(READY_CLASS);
      return;
    }

    const atTop = window.scrollY <= AT_TOP;
    const widthChanged = !snapshot || snapshot.width !== innerWidth;
    // Scrolled page + same width: keep what was measured at the top.
    if (snapshot && !atTop && !widthChanged) return;

    // 1) Natural in-flow position of the section, measured with no pull-up
    //    applied (idempotent: never depends on the previous value).
    html.style.setProperty('--leblu-header-overlap', '0px');
    const naturalTop = outer.getBoundingClientRect().top + window.scrollY;

    // 2) Where the gallery must start: right below the announcement bar.
    const bar = findAnnouncement();
    const barBottom = bar ? pageBottom(bar) : 0;
    const top = Math.max(0, Math.min(naturalTop, barBottom));
    const overlap = Math.max(0, naturalTop - top);

    // 3) Panel starts below the (translucent) navigation.
    const panelTop = Math.max(92, getNavigationBottom() - top + 16);

    html.style.setProperty('--leblu-header-overlap', `${Math.round(overlap)}px`);
    html.style.setProperty('--leblu-top-offset', `${Math.round(top)}px`);
    html.style.setProperty('--leblu-panel-top', `${Math.round(panelTop)}px`);
    html.style.removeProperty('--leblu-viewport-height'); // legacy variable, no longer used

    snapshot = { width: innerWidth, provisional: !atTop };

    if (DEBUG) {
      console.table({
        scrollY: Math.round(window.scrollY), innerWidth, innerHeight,
        naturalTop: Math.round(naturalTop), barBottom: Math.round(barBottom),
        overlap: Math.round(overlap), topOffset: Math.round(top), panelTop: Math.round(panelTop),
        provisional: snapshot.provisional
      });
    }
  }

  function fitPanel() {
    const product = $('.leblu-new-product');
    const panel = product && $('.leblu-new-panel', product);
    const details = panel && $('.product__section-details', panel);
    if (!details || !panel || innerWidth <= MOBILE_MAX) return;
    details.style.removeProperty('--leblu-fit');
    details.style.transform = 'none';
  }

  function layout() {
    measureGeometry();
    fitPanel();
  }

  function scheduleGeometry() {
    if (geometryFrame) cancelAnimationFrame(geometryFrame);
    geometryFrame = requestAnimationFrame(layout);
  }

  // Reveal once, after the first synchronous measurement and (briefly) the web fonts.
  function revealWhenStable() {
    if (revealed) return;
    revealed = true;
    const html = document.documentElement;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      layout();
      html.classList.add(READY_CLASS);
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(finish, finish);
      setTimeout(finish, REVEAL_TIMEOUT);
    } else {
      finish();
    }
  }

  function bindOnce() {
    if (bound) return;
    bound = true;
    addEventListener('resize', scheduleGeometry, { passive: true });
    addEventListener('load', scheduleGeometry);
    addEventListener('pageshow', scheduleGeometry);
    // If the first measurement happened on a scrolled page (restored scroll),
    // redo it as soon as the user is back at the top.
    addEventListener('scroll', () => {
      if (snapshot && snapshot.provisional && window.scrollY <= AT_TOP) scheduleGeometry();
    }, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleGeometry);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(scheduleGeometry);
      [findAnnouncement(), $('#shopify-section-header'), $('header.header__announcement')]
        .filter(Boolean)
        .forEach(el => observer.observe(el));
    }
  }

  function initDescriptions() {
    document.querySelectorAll('[data-leblu-description]').forEach(wrapper => {
      if (wrapper.dataset.lebluReady) return;
      wrapper.dataset.lebluReady = '1';
      const content = $('.leblu-new-description-text', wrapper);
      const button = $('[data-leblu-description-toggle]', wrapper);
      if (!content || !button) return;
      requestAnimationFrame(() => { button.hidden = content.scrollHeight <= content.clientHeight + 2; });
      button.addEventListener('click', () => {
        const expanded = wrapper.classList.toggle('is-expanded');
        button.textContent = expanded ? '\u2212' : '+';
        button.setAttribute('aria-expanded', String(expanded));
        button.setAttribute('aria-label', expanded ? 'Riduci la descrizione' : 'Espandi la descrizione');
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-leblu-gallery]').forEach(setupGallery);
    initDescriptions();
    layout();          // synchronous first measurement
    bindOnce();
    revealWhenStable();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  // Theme editor re-renders the section: measure again from scratch.
  document.addEventListener('shopify:section:load', () => {
    snapshot = null;
    init();
  });
})();